import { useEffect, useState } from 'react';
import {
  RiAddLine,
  RiCheckLine,
  RiDeleteBin6Line,
  RiPlugLine,
  RiRefreshLine,
  RiServerLine,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api } from '../state/api';
import { useStore } from '../state/store';
import { cn } from '@/lib/utils';

const EMPTY = {
  id: '',
  name: '',
  baseUrl: '',
  apiKey: '',
  models: '',
  defaultModel: '',
  enabled: true,
};

/** AI 供应商管理（仅平台管理员）。支持任意 OpenAI 兼容端点与多模型。 */
export function ProviderDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const providers = useStore(state => state.aiProviders);
  const refreshAiProviders = useStore(state => state.refreshAiProviders);
  const toast = useStore(state => state.toast);

  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);

  useEffect(() => {
    if (open) void refreshAiProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    if (!form.name.trim() || !form.baseUrl.trim()) return;
    setBusy(true);
    const models = form.models
      .split(/[,，\n\s]+/)
      .map(item => item.trim())
      .filter(Boolean);
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      baseUrl: form.baseUrl.trim(),
      models,
      defaultModel: form.defaultModel.trim() || models[0] || '',
      enabled: form.enabled,
    };
    if (form.apiKey) payload.apiKey = form.apiKey;
    else if (!editing) payload.apiKey = '';

    try {
      if (editing) {
        await api.updateAiProvider(editing, payload);
        toast('success', '供应商已更新');
      } else {
        if (!form.id.trim()) {
          toast('error', '请填写 id（字母数字与短横线）');
          setBusy(false);
          return;
        }
        await api.createAiProvider({ ...payload, id: form.id.trim() });
        toast('success', '供应商已添加');
      }
      setForm(EMPTY);
      setEditing(null);
      await refreshAiProviders();
    } catch (error) {
      toast('error', (error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const test = async (id: string) => {
    setTesting(id);
    try {
      const list = await api.aiModels(id);
      toast(
        list.error ? 'info' : 'success',
        list.error
          ? `拉取失败：${list.error}`
          : `「${list.providerName}」可用模型 ${list.models.length} 个（${
              list.source === 'remote' ? '远端' : '配置'
            }）`
      );
      await refreshAiProviders();
    } catch (error) {
      toast('error', (error as Error).message);
    } finally {
      setTesting(null);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm(`确认删除供应商「${id}」？`)) return;
    try {
      await api.deleteAiProvider(id);
      toast('success', '供应商已删除');
      await refreshAiProviders();
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogContent className="w-[min(780px,95vw)]">
        <DialogHeader>
          <DialogTitle>AI 供应商</DialogTitle>
          <DialogDescription>
            任意 OpenAI 兼容端点均可接入（官方 / 中转 / 自建聚合网关）。密钥只保存在服务端，
            接口仅回传掩码；模型清单可点「探测」从远端拉取。
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-[var(--radius)] border border-border bg-muted/40 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[11.5px] font-medium">
            <RiAddLine className="size-3.5 text-primary" />
            {editing ? `编辑：${editing}` : '新增供应商'}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1.5">
              <Label>id *（唯一标识）</Label>
              <Input
                value={form.id}
                disabled={Boolean(editing)}
                placeholder="openai / deepseek / relay"
                onChange={e => setForm({ ...form, id: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>显示名称 *</Label>
              <Input
                value={form.name}
                placeholder="OpenAI 官方"
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label>Base URL *（OpenAI 兼容根地址）</Label>
              <Input
                value={form.baseUrl}
                placeholder="https://api.openai.com/v1"
                onChange={e => setForm({ ...form, baseUrl: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>API Key{editing ? '（留空表示不修改）' : ''}</Label>
              <Input
                type="password"
                value={form.apiKey}
                placeholder="sk-…"
                onChange={e => setForm({ ...form, apiKey: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>默认模型</Label>
              <Input
                value={form.defaultModel}
                placeholder="gpt-4o-mini"
                onChange={e => setForm({ ...form, defaultModel: e.target.value })}
              />
            </div>
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label>模型清单（逗号分隔；留空可点探测自动拉取）</Label>
              <Input
                value={form.models}
                placeholder="gpt-4o-mini, gpt-4o, o3-mini"
                onChange={e => setForm({ ...form, models: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={e => setForm({ ...form, enabled: e.target.checked })}
              />
              启用
            </label>
            <div className="ml-auto flex gap-2">
              {editing && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditing(null);
                    setForm(EMPTY);
                  }}
                >
                  取消编辑
                </Button>
              )}
              <Button size="sm" disabled={busy} onClick={submit}>
                {editing ? '保存' : '添加'}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="label-caps">已配置（{providers.length}）</span>
          <Button size="xs" variant="ghost" onClick={() => void refreshAiProviders()}>
            <RiRefreshLine className="size-3" />
            刷新
          </Button>
        </div>

        <div className="mt-2 flex max-h-[260px] flex-col gap-1.5 overflow-y-auto">
          {providers.map(provider => (
            <div
              key={provider.id}
              className={cn(
                'flex items-center gap-2.5 rounded-[var(--radius)] border p-2.5',
                provider.enabled ? 'border-border bg-card' : 'border-border bg-muted/40 opacity-75'
              )}
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-[7px] bg-muted">
                <RiServerLine className="size-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[12px] font-medium">{provider.name}</span>
                  <Badge variant={provider.source === 'env' ? 'muted' : 'default'} className="shrink-0">
                    {provider.source === 'env' ? '环境变量' : '界面配置'}
                  </Badge>
                  {provider.hasKey ? (
                    <Badge variant="evidence" className="shrink-0 gap-1">
                      <RiCheckLine className="size-3" />
                      {provider.keyHint}
                    </Badge>
                  ) : (
                    <Badge variant="cord" className="shrink-0">
                      未配置密钥
                    </Badge>
                  )}
                  <span className="shrink-0 font-mono text-[9.5px] text-muted-foreground">
                    {provider.models.length} 模型
                  </span>
                </div>
                <div className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
                  {provider.id} · {provider.baseUrl}
                </div>
              </div>
              <Button
                size="xs"
                variant="outline"
                className="shrink-0"
                disabled={testing === provider.id}
                onClick={() => test(provider.id)}
              >
                <RiPlugLine className={cn('size-3', testing === provider.id && 'animate-pulse')} />
                探测
              </Button>
              {provider.source === 'db' && (
                <>
                  <Button
                    size="xs"
                    variant="ghost"
                    className="shrink-0"
                    onClick={() => {
                      setEditing(provider.id);
                      setForm({
                        id: provider.id,
                        name: provider.name,
                        baseUrl: provider.baseUrl,
                        apiKey: '',
                        models: provider.models.join(', '),
                        defaultModel: provider.defaultModel,
                        enabled: provider.enabled,
                      });
                    }}
                  >
                    编辑
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    className="shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(provider.id)}
                  >
                    <RiDeleteBin6Line className="size-3.5" />
                  </Button>
                </>
              )}
            </div>
          ))}
          {providers.length === 0 && (
            <div className="rounded-[var(--radius)] border border-dashed border-border p-5 text-center text-[11.5px] text-muted-foreground">
              尚未配置供应商，研判将使用本地启发式引擎。
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}